import { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Plus, Minus, Trash2, ArrowLeft } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

interface Product {
  id: number;
  name: string;
  price: number;
  image: string;
  category: string;
}

interface CartItem extends Product {
  quantity: number;
}

const products: Product[] = [
  { id: 1, name: 'Laptop Gaming', price: 15000000, image: '🖥️', category: 'Electronics' },
  { id: 2, name: 'Mouse Wireless', price: 250000, image: '🖱️', category: 'Accessories' },
  { id: 3, name: 'Keyboard Mechanical', price: 850000, image: '⌨️', category: 'Accessories' },
  { id: 4, name: 'Headset Gaming', price: 1200000, image: '🎧', category: 'Accessories' },
  { id: 5, name: 'Monitor 4K', price: 4500000, image: '🖥️', category: 'Electronics' },
  { id: 6, name: 'Webcam HD', price: 750000, image: '📷', category: 'Electronics' },
];

export default function ECommerceDemo() {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);

  const addToCart = (product: Product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    toast.success(`${product.name} ditambahkan ke keranjang!`);
  };

  const updateQuantity = (id: number, delta: number) => {
    setCart((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item
      )
    );
  };

  const removeFromCart = (id: number) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
    toast.success('Item dihapus dari keranjang');
  };

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <div className="min-h-screen bg-dark p-4">
      <Toaster position="top-right" />
      
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="glass-card p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/#projects" className="text-neon-blue hover:text-neon-green transition">
              <ArrowLeft size={24} />
            </a>
            <div>
              <h1 className="text-2xl font-bold text-white">Mini E-Commerce</h1>
              <p className="text-sm text-gray-400">Demo Aplikasi Belanja Online (Simulasi)</p>
            </div>
          </div>
          <button
            onClick={() => setShowCart(!showCart)}
            className="relative btn-neon flex items-center gap-2"
          >
            <ShoppingCart size={20} />
            Cart ({cart.length})
            {cart.length > 0 && (
              <span className="absolute -top-2 -right-2 w-6 h-6 bg-neon-green rounded-full flex items-center justify-center text-xs">
                {cart.reduce((sum, item) => sum + item.quantity, 0)}
              </span>
            )}
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Products Grid */}
          <div className={`${showCart ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="glass-card p-4"
                >
                  <div className="text-6xl text-center mb-4">{product.image}</div>
                  <h3 className="font-bold text-white mb-2">{product.name}</h3>
                  <p className="text-neon-blue font-bold mb-4">
                    Rp {product.price.toLocaleString('id-ID')}
                  </p>
                  <button
                    onClick={() => addToCart(product)}
                    className="w-full btn-neon-green text-sm py-2"
                  >
                    Tambah ke Keranjang
                  </button>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Cart Sidebar */}
          {showCart && (
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              className="glass-card p-6"
            >
              <h2 className="text-xl font-bold text-white mb-4">Keranjang</h2>
              {cart.length === 0 ? (
                <p className="text-gray-400 text-center py-8">Keranjang kosong</p>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map((item) => (
                      <div key={item.id} className="bg-dark-lighter p-3 rounded-xl">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-3xl">{item.image}</span>
                          <div className="flex-1">
                            <h4 className="text-white font-semibold text-sm">{item.name}</h4>
                            <p className="text-neon-blue text-xs">
                              Rp {item.price.toLocaleString('id-ID')}
                            </p>
                          </div>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-500 hover:text-red-400"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.id, -1)}
                            className="w-8 h-8 rounded-lg bg-dark flex items-center justify-center text-neon-blue hover:bg-dark/50"
                          >
                            <Minus size={16} />
                          </button>
                          <span className="flex-1 text-center text-white font-bold">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, 1)}
                            className="w-8 h-8 rounded-lg bg-dark flex items-center justify-center text-neon-blue hover:bg-dark/50"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-700 pt-4">
                    <div className="flex justify-between text-white font-bold mb-4">
                      <span>Total:</span>
                      <span className="text-neon-green">
                        Rp {total.toLocaleString('id-ID')}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        toast.custom((t) => (
                          <div className="glass-card p-4 max-w-md">
                            <p className="text-white font-bold mb-2">Demo Mode - Checkout Simulasi</p>
                            <p className="text-sm text-gray-400 mb-3">
                              Ini adalah demo mode. Untuk implementasi production, payment gateway seperti Stripe, PayPal, atau Midtrans perlu diintegrasikan.
                            </p>
                            <p className="text-neon-green text-sm">✓ Cart berhasil dikosongkan</p>
                          </div>
                        ), { duration: 4000 });
                        setCart([]);
                      }}
                      className="w-full btn-neon-green"
                    >
                      Checkout (Demo)
                    </button>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Payment gateway nyata akan diimplementasikan di production
                    </p>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
